// Apolo IDE — SidePanel: Plan tab (Epic 10).
//
// Shows the current session's plan-mode status + the latest proposed plan
// (if any). User can toggle plan mode on/off from here without waiting for
// the agent to call ExitPlanMode.
//
// SOURCE-OF-TRUTH (Plan Mode v2, E5): the LIVE plan flow is driven SOLELY by the
// composer Plan chip → `ui.plan_mode` (local, read by handleSend to fire
// planPropose → read-only loop → present_plan). This tab is intentionally a
// SEPARATE concern: server-side, SESSION-LEVEL persistence via
// `togglePlanMode` → `PUT /api/code-ide/sessions/:id/plan` → `mode_state`.
// They are COHERENT-by-separation, not competing: this toggle does NOT touch
// `ui.plan_mode` (and so cannot trigger the live propose flow from a panel
// click), and the chip does NOT write `mode_state`. Keep it that way — wiring
// this toggle into `ui.plan_mode` would let a panel click silently kick off a
// planPropose turn. See useAgentStore.togglePlanMode + planStateStore.js.

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ClipboardList, Loader2, XCircle } from 'lucide-react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { apiFetch } from '../../../lib/api.js';

export default function PlanTab() {
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const togglePlanMode = useAgentStore((s) => s.togglePlanMode);
  const [state, setState] = useState({ loading: false, planMode: false, latest: null, error: null });

  const refresh = useCallback(async () => {
    if (!activeSessionId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    const res = await apiFetch(`/api/code-ide/sessions/${activeSessionId}/plan`);
    if (!res.ok) {
      setState((s) => ({ ...s, loading: false, error: res.error || 'falha' }));
      return;
    }
    setState({
      loading: false,
      planMode: res.data.planMode,
      latest: res.data.latest_plan,
      error: null,
    });
  }, [activeSessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleToggle() {
    if (!activeSessionId) return;
    const res = await togglePlanMode(activeSessionId, !state.planMode);
    if (res.ok) {
      setState((s) => ({ ...s, planMode: res.planMode }));
    }
  }

  if (!activeSessionId) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-6 text-center">
        <p className="max-w-xs text-sm text-apolo-textTertiary">
          Abra uma sessão para usar o plan mode.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 px-4 py-4 text-sm">
      <header className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-apolo-textPrimary">
          <ClipboardList size={14} aria-hidden="true" />
          Plan mode
        </h3>
        <span
          className={
            'rounded-full px-2 py-0.5 text-xs font-medium ' +
            (state.planMode
              ? 'bg-apolo-warning/15 text-apolo-warning'
              : 'bg-apolo-success/15 text-apolo-success')
          }
        >
          {state.planMode ? 'PLAN' : 'EXECUTE'}
        </span>
      </header>

      <p className="text-xs text-apolo-textTertiary">
        Em plan mode todas as escritas (multi-edit, file_write, shell) são bloqueadas no servidor.
        Use <code>ExitPlanMode</code> ou o botão abaixo pra sair.
      </p>

      <button
        type="button"
        onClick={handleToggle}
        disabled={state.loading}
        className="inline-flex items-center justify-center gap-1.5 rounded-md border border-apolo-border px-3 py-1.5 text-sm transition hover:bg-apolo-surface2 disabled:opacity-60"
      >
        {state.loading && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
        {state.planMode ? 'Sair do plan mode' : 'Entrar em plan mode'}
      </button>

      {state.error && (
        <div role="alert" className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-3 py-2 text-xs text-apolo-error">
          {state.error}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <span className="text-xs uppercase text-apolo-textTertiary">Último plano</span>
        {state.latest ? (
          <div className="flex flex-col gap-1.5 rounded-md border border-apolo-border bg-apolo-surface2 p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-apolo-textTertiary">{state.latest.created_at}</span>
              {state.latest.approved ? (
                <span className="flex items-center gap-1 text-apolo-success">
                  <CheckCircle2 size={12} aria-hidden="true" />
                  aprovado
                </span>
              ) : (
                <span className="flex items-center gap-1 text-apolo-textTertiary">
                  <XCircle size={12} aria-hidden="true" />
                  pendente
                </span>
              )}
            </div>
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-apolo-textSecondary">
              {state.latest.plan}
            </pre>
          </div>
        ) : (
          <p className="text-xs text-apolo-textTertiary">Nenhum plano registrado ainda.</p>
        )}
      </div>
    </div>
  );
}
